import type { ChangeEvent } from 'react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, TextField } from '@mui/material';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { observer } from 'mobx-react';
import { Button } from '@components/index';
import { Tooltip } from '@components/tooltip/Tooltip';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ErrorIcon } from '@icons/ErrorIcon';
import { InfoIcon } from '@icons/InfoIcon';
import { PersonIcon } from '@icons/PersonIcon';
import { SearchIcon } from '@icons/SearchIcon';
import type {
  ProductSubscriptionActionType,
  ProductSubscriptionFilter,
  ProductSubscriptionSortString,
} from '@repositories/productSubscriptionRepository/productSubscription.types';
import { useSearchParamsFilter } from '@customHooks/useSearchParamsFilter';
import { useProductRequestVendor } from '@queryHooks/useProductRequest';
import { useGetProductSubscriptionListInfinite } from '@queryHooks/useProductSubscription';
import { useSaasOverview } from '@queryHooks/useSaasOverview';
import { DomUtil } from '@utils/DomUtil';
import { formatCompactCurrency, numberFormat } from '@utils/numberFormat';
import { FilterSelect as VendorFilterSelect } from '../userMgmt/list/FilterSelect';
import { SaasOverviewCard } from './SaasOverviewCard';
import { SubscriptionTable } from './subscriptionTable';
import { AddExistingSubscriptionModal } from './subscriptionTable/addExistingSubscriptionModal';

/**
 * SaaSOps 대시보드 카드. BE `GET /statistics/product-subscription-overview` 응답 기반.
 * 사용률·연 비용 필터는 카드 클릭 시 popover에서 직접 입력.
 */
type SaasCardId =
  | 'totalCount'
  | 'annualSpend'
  | 'expiring30'
  | 'expiring60'
  | 'expiring90'
  | 'underutilized'
  | 'actionRequired';

type SaasCardPosition = 'totalCount' | 'annualSpend' | 'expiring' | 'underutilized' | 'actionRequired';

const SAAS_CARD_POSITIONS: readonly SaasCardPosition[] = [
  'totalCount',
  'annualSpend',
  'expiring',
  'underutilized',
  'actionRequired',
];

const ALL_SAAS_CARD_IDS: readonly SaasCardId[] = [
  'totalCount',
  'annualSpend',
  'expiring30',
  'expiring60',
  'expiring90',
  'underutilized',
  'actionRequired',
];

const toCardPosition = (id: SaasCardId): SaasCardPosition =>
  id === 'expiring30' || id === 'expiring60' || id === 'expiring90' ? 'expiring' : id;

const isSaasCardId = (value: unknown): value is SaasCardId =>
  typeof value === 'string' && (ALL_SAAS_CARD_IDS as readonly string[]).includes(value);

const DEFAULT_EXPIRING_WINDOW_DAYS = 30;
const EXPIRING_WINDOWS: readonly { id: 'expiring30' | 'expiring60' | 'expiring90'; days: number }[] = [
  { id: 'expiring30', days: 30 },
  { id: 'expiring60', days: 60 },
  { id: 'expiring90', days: 90 },
];
type ActionRequiredFieldKey = 'usageWorkflowFailedCount' | 'optimizableLicenseCount';

const normalizeActionTypesParam = (value: unknown): ProductSubscriptionActionType[] => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.filter(Boolean) as ProductSubscriptionActionType[];
  return [value as ProductSubscriptionActionType];
};

type ActionRequiredTypeMeta = {
  actionType: ProductSubscriptionActionType;
  iconType: 'usageWorkflowFailed' | 'optimizableLicense';
  labelKey: string;
  tooltipKey: string;
  iconColorKey: 'ic-red-light' | 'ic-gray-main';
};

/**
 * 처리 대기 카드 유형별 표시 메타.
 * overview 필드 ↔ ProductSubscriptionFilterDTO.actionTypes
 */
const ACTION_REQUIRED_TYPE_META: Record<ActionRequiredFieldKey, ActionRequiredTypeMeta> = {
  usageWorkflowFailedCount: {
    actionType: 'USAGE_WORKFLOW_FAILED',
    iconType: 'usageWorkflowFailed',
    labelKey: 'Subscrib_Main_Overview_Action_Type_UsageWorkflowFailed',
    tooltipKey: 'Subscrib_Main_Overview_Action_Type_UsageWorkflowFailed_Tooltip',
    iconColorKey: 'ic-red-light',
  },
  optimizableLicenseCount: {
    actionType: 'OPTIMIZABLE_LICENSE',
    iconType: 'optimizableLicense',
    labelKey: 'Subscrib_Main_Overview_Action_Type_OptimizableLicense',
    tooltipKey: 'Subscrib_Main_Overview_Action_Type_OptimizableLicense_Tooltip',
    iconColorKey: 'ic-gray-main',
  },
};

const ACTION_REQUIRED_ICON_SIZE = 14;

const ActionRequiredIcon = ({ iconType, color }: { iconType: ActionRequiredTypeMeta['iconType']; color: string }) => {
  if (iconType === 'usageWorkflowFailed') {
    return <ErrorIcon width={ACTION_REQUIRED_ICON_SIZE} height={ACTION_REQUIRED_ICON_SIZE} color={color} />;
  }
  return <PersonIcon width={ACTION_REQUIRED_ICON_SIZE} height={ACTION_REQUIRED_ICON_SIZE} color={color} />;
};

const ACTION_REQUIRED_FIELD_ORDER: readonly ActionRequiredFieldKey[] = [
  'usageWorkflowFailedCount',
  'optimizableLicenseCount',
];

const FIXED_UNDERUTILIZATION_THRESHOLD = 0.3;

export const SubscriptionList: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const { authStore } = useStore();
  const [scrollTarget, setScrollTarget] = useState<HTMLElement | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const [queries, setQueries, clearQueries] = useSearchParamsFilter<
    ProductSubscriptionFilter & { filterType?: string }
  >({
    defaultFilter: {
      keyword: undefined,
      sort: [],
      filterType: undefined,
      productVendorName: undefined,
    },
    whiteList: [
      'sort',
      'keyword',
      'nextExpiryDateFrom',
      'nextExpiryDateTo',
      'status',
      'filterType',
      'productVendorName',
      'utilizationRateFrom',
      'utilizationRateTo',
      'annualPaymentEstimateFrom',
      'annualPaymentEstimateTo',
      'actionRequired',
      'actionTypes',
    ],
  });

  const [clickedType, setClickedType] = useState<SaasCardId>(() =>
    isSaasCardId(queries.filterType) ? queries.filterType : 'totalCount',
  );

  const [keywordInput, setKeywordInput] = useState(queries.keyword ?? '');

  const { data: saasOverview, isSuccess: isOverviewSuccess } = useSaasOverview(FIXED_UNDERUTILIZATION_THRESHOLD);

  const {
    data: productRequestVendorList,
    isFetchingNextPage: isProductVendorFetchingNextPage,
    hasNextPage: isProductVendorHasNextPage,
    fetchNextPage: fetchNextProductVendorPage,
    isLoading: isProductVendorLoading,
  } = useProductRequestVendor({ page: 0, size: 100 });

  const productVendorOptions = useMemo(
    () =>
      (productRequestVendorList?.pages.flatMap(({ content }) => content) ?? []).map((item: string) => ({
        value: item ?? '',
        name: item ?? '',
      })),
    [productRequestVendorList],
  );

  const {
    isLoading: isSubscriptionListPageLoading,
    data: subscriptionListPage,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetProductSubscriptionListInfinite({ ...queries, includesBillingStat: true });

  const selectedActionTypes = useMemo(
    () => normalizeActionTypesParam(queries.actionTypes),
    [queries.actionTypes],
  );

  const productList = useMemo(
    () => subscriptionListPage?.pages.flatMap(({ content }) => content) ?? [],
    [subscriptionListPage],
  );

  // filterType이 URL에서 복원될 때 날짜/사용률 필터를 현재 시점 기준으로 재계산
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    const filterType = queries.filterType;
    if (!isSaasCardId(filterType) || filterType === 'totalCount') return;

    const expiringWindow = EXPIRING_WINDOWS.find(w => w.id === filterType);
    if (expiringWindow) {
      setQueries(prev => ({
        ...prev,
        nextExpiryDateFrom: dayjs().add(1, 'day').format('YYYY-MM-DD'),
        nextExpiryDateTo: dayjs().add(expiringWindow.days, 'day').format('YYYY-MM-DD'),
      }));
    }
  }, [queries.filterType, setQueries]);

  const applyExpiringFilter = (days: 30 | 60 | 90) => {
    const targetId = EXPIRING_WINDOWS.find(w => w.days === days)?.id ?? 'expiring30';
    setQueries(prev => ({
      ...prev,
      status: undefined,
      utilizationRateFrom: undefined,
      utilizationRateTo: undefined,
      annualPaymentEstimateFrom: undefined,
      annualPaymentEstimateTo: undefined,
      actionRequired: undefined,
      actionTypes: undefined,
      nextExpiryDateFrom: dayjs().add(1, 'day').format('YYYY-MM-DD'),
      nextExpiryDateTo: dayjs().add(days, 'day').format('YYYY-MM-DD'),
      filterType: targetId,
    }));
    setClickedType(targetId);
  };

  const handleCardClick = (cardId: SaasCardId) => () => {
    if (cardId === 'totalCount') {
      clearQueries();
      setClickedType('totalCount');
      return;
    }

    if (cardId === 'annualSpend') {
      applyAnnualSpendFilter();
      return;
    }

    if (cardId === 'underutilized') {
      applyUnderutilizedFilter();
      return;
    }

    if (cardId === 'expiring30' || cardId === 'expiring60' || cardId === 'expiring90') {
      const windowDays = EXPIRING_WINDOWS.find(w => w.id === cardId)?.days ?? DEFAULT_EXPIRING_WINDOW_DAYS;
      applyExpiringFilter(windowDays as 30 | 60 | 90);
      return;
    }

    if (cardId === 'actionRequired') {
      applyActionRequiredCardFilter();
    }
  };

  const applyAnnualSpendFilter = () => {
    setQueries(prev => ({
      ...prev,
      nextExpiryDateFrom: undefined,
      nextExpiryDateTo: undefined,
      status: undefined,
      utilizationRateFrom: undefined,
      utilizationRateTo: undefined,
      actionRequired: undefined,
      actionTypes: undefined,
      annualPaymentEstimateFrom: 1,
      annualPaymentEstimateTo: undefined,
      sort: ['annualPaymentEstimate.desc'] as ProductSubscriptionSortString[],
      filterType: 'annualSpend',
    }));
    setClickedType('annualSpend');
  };

  const applyUnderutilizedFilter = () => {
    setQueries(prev => ({
      ...prev,
      nextExpiryDateFrom: undefined,
      nextExpiryDateTo: undefined,
      status: undefined,
      annualPaymentEstimateFrom: undefined,
      annualPaymentEstimateTo: undefined,
      actionRequired: undefined,
      actionTypes: undefined,
      utilizationRateFrom: undefined,
      utilizationRateTo: FIXED_UNDERUTILIZATION_THRESHOLD,
      filterType: 'underutilized',
    }));
    setClickedType('underutilized');
  };

  /** 카드 본체: 조치 필요만. 유형 기본값 없음(BE 스코핑). */
  const applyActionRequiredCardFilter = () => {
    setQueries(prev => ({
      ...prev,
      nextExpiryDateFrom: undefined,
      nextExpiryDateTo: undefined,
      status: undefined,
      utilizationRateFrom: undefined,
      utilizationRateTo: undefined,
      annualPaymentEstimateFrom: undefined,
      annualPaymentEstimateTo: undefined,
      actionRequired: true,
      actionTypes: undefined,
      filterType: 'actionRequired',
    }));
    setClickedType('actionRequired');
  };

  /** 칩: 조치 유형 다중 선택(토글). */
  const toggleActionTypeChip = (type: ProductSubscriptionActionType) => {
    setQueries(prev => {
      const current = normalizeActionTypesParam(prev.actionTypes);
      const nextTypes = current.includes(type)
        ? current.filter(t => t !== type)
        : [...current, type];

      return {
        ...prev,
        nextExpiryDateFrom: undefined,
        nextExpiryDateTo: undefined,
        status: undefined,
        utilizationRateFrom: undefined,
        utilizationRateTo: undefined,
        annualPaymentEstimateFrom: undefined,
        annualPaymentEstimateTo: undefined,
        actionRequired: true,
        actionTypes: nextTypes.length > 0 ? nextTypes : undefined,
        filterType: 'actionRequired',
      };
    });
    setClickedType('actionRequired');
  };

  const handleTableFilterUpdate = (filter: Partial<ProductSubscriptionFilter>) => {
    // 테이블 헤더에서 정렬/필터 변경 시: URL에 남아있는 카드 필터(만료/상태/사용률/연 결제/처리 대기)도 모두 해제하고
    // 헤더가 보낸 키만 유지 — `...filter`를 마지막에 spread하여 헤더 값이 우선 적용되도록 한다.
    setClickedType('totalCount');
    setQueries(prev => ({
      ...prev,
      nextExpiryDateFrom: undefined,
      nextExpiryDateTo: undefined,
      status: undefined,
      utilizationRateFrom: undefined,
      utilizationRateTo: undefined,
      annualPaymentEstimateFrom: undefined,
      annualPaymentEstimateTo: undefined,
      actionRequired: undefined,
      actionTypes: undefined,
      filterType: undefined,
      ...filter,
    }));
  };

  const handleEndReached = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  const getCardClassName = (cardId: SaasCardId, index: number) => {
    const clickedPos = toCardPosition(clickedType);
    const currentPos = toCardPosition(cardId);
    if (clickedPos === currentPos) {
      return 'clicked';
    }

    const clickedIndex = SAAS_CARD_POSITIONS.findIndex(pos => pos === clickedPos);
    if (clickedIndex - 1 === index) {
      return 'prevButton';
    }

    return '';
  };

  const debouncedSetKeyword = useMemo(
    () =>
      debounce((keyword: string | undefined) => {
        setQueries(prev => ({ ...prev, keyword }));
      }, 500),
    [setQueries],
  );

  useEffect(() => {
    setKeywordInput(queries.keyword ?? '');
  }, [queries.keyword]);

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const handleKeywordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setKeywordInput(value);
    debouncedSetKeyword(value || undefined);
  };

  const handleChangeProductVendor = useCallback(
    (value: { value: string; name: string } | null) => {
      setQueries(prev => ({ ...prev, productVendorName: value?.value ? value.value : undefined }));
    },
    [setQueries],
  );

  const handleScrollProductVendor = (event: React.UIEvent<HTMLUListElement>) => {
    if (
      DomUtil.isScrollBottom(event.target as HTMLUListElement) &&
      !isProductVendorFetchingNextPage &&
      isProductVendorHasNextPage
    ) {
      fetchNextProductVendorPage();
    }
  };

  const handleAddModalOpen = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);
  const handleAddModalClose = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  return (
    <>
      <Container ref={setScrollTarget}>
        <Content>
          <SectionTitleWrapper>
            <SectionTitle>{t('Subscription.menu.list')}</SectionTitle>
            <Tooltip label={t('Subscrib_Main_TableGuide')} placement="bottom-right">
              <InfoIconWrapper>
                <InfoIcon width={16} height={16} color={colors['ic-gray-light']} />
              </InfoIconWrapper>
            </Tooltip>
          </SectionTitleWrapper>
          <CardContainer>
            {isOverviewSuccess && saasOverview ? (
              <>
                <SaasOverviewCard
                  id="totalCount"
                  title={t('Subscrib_Main_Overview_Total_Title')}
                  value={numberFormat({ num: saasOverview.totalProductCount })}
                  subLabel={t('Subscrib_Main_Overview_Total_Sub', {
                    count: saasOverview.totalSubscriptionCount,
                  })}
                  onClick={handleCardClick('totalCount')}
                  className={getCardClassName('totalCount', 0)}
                />
                <SaasOverviewCard
                  id="annualSpend"
                  title={t('Subscrib_Main_Overview_Spend_Title')}
                  value={formatCompactCurrency(saasOverview.annualSpendEstimate.totalAmount, i18n.language)}
                  subLabel={t('Subscrib_Main_Overview_Spend_Sub_Yoy')}
                  trendValue={saasOverview.annualSpendEstimate.yearOverYearRate}
                  badgeColor="#28A745"
                  onClick={handleCardClick('annualSpend')}
                  className={getCardClassName('annualSpend', 1)}
                />
                <SaasOverviewCard
                  id="expiring"
                  title={t('Subscrib_Main_Overview_Expiring_Title')}
                  value={numberFormat({
                    num:
                      saasOverview.expiringInDays.within30 +
                      saasOverview.expiringInDays.within60 +
                      saasOverview.expiringInDays.within90,
                  })}
                  badgeColor="#F9C513"
                  inlineItems={[
                    {
                      label: t('Subscrib_Main_Overview_Expiring_30'),
                      count: saasOverview.expiringInDays.within30,
                      onClick: () => applyExpiringFilter(30),
                      active: clickedType === 'expiring30',
                    },
                    {
                      label: t('Subscrib_Main_Overview_Expiring_60'),
                      count: saasOverview.expiringInDays.within60,
                      onClick: () => applyExpiringFilter(60),
                      active: clickedType === 'expiring60',
                    },
                    {
                      label: t('Subscrib_Main_Overview_Expiring_90'),
                      count: saasOverview.expiringInDays.within90,
                      onClick: () => applyExpiringFilter(90),
                      active: clickedType === 'expiring90',
                    },
                  ]}
                  onClick={handleCardClick('expiring30')}
                  className={getCardClassName('expiring30', 2)}
                />
                <SaasOverviewCard
                  id="underutilized"
                  title={t('Subscrib_Main_Overview_Underutilized_Title')}
                  value={numberFormat({ num: saasOverview.underutilized.count })}
                  subLabel={t('Subscrib_Main_Overview_Underutilized_Sub', {
                    threshold: Math.round(saasOverview.underutilized.threshold * 100),
                  })}
                  badgeColor="#EA4A5A"
                  onClick={handleCardClick('underutilized')}
                  className={getCardClassName('underutilized', 3)}
                />
                <SaasOverviewCard
                  id="actionRequired"
                  title={t('Subscrib_Main_Overview_Action_Title')}
                  value={numberFormat({ num: saasOverview.actionRequired.totalCount })}
                  inlineItems={ACTION_REQUIRED_FIELD_ORDER.map(fieldKey => {
                    const meta = ACTION_REQUIRED_TYPE_META[fieldKey];
                    return {
                      icon: <ActionRequiredIcon iconType={meta.iconType} color={colors[meta.iconColorKey]} />,
                      label: t(meta.labelKey),
                      count: saasOverview.actionRequired[fieldKey],
                      onClick: () => toggleActionTypeChip(meta.actionType),
                      active: selectedActionTypes.includes(meta.actionType),
                      tooltip: t(meta.tooltipKey),
                    };
                  })}
                  isDanger
                  onClick={handleCardClick('actionRequired')}
                  className={getCardClassName('actionRequired', 4)}
                />
              </>
            ) : null}
          </CardContainer>
          <HeaderContainer>
            <LeftContainer>
              <NumOfSubscription>
                {t('Acc_Main_12')}
                <span>{subscriptionListPage?.pages[0].totalElements}</span>
              </NumOfSubscription>
              <VendorFilterSelect
                id="productVendorList"
                data={productVendorOptions}
                onChange={handleChangeProductVendor}
                placeholder={t('Subscrib_Main_Filter_Vendor')}
                onScroll={handleScrollProductVendor}
                isLoading={isProductVendorLoading}
                value={{
                  value: queries.productVendorName ?? '',
                  name: queries.productVendorName ?? '',
                }}
              />
            </LeftContainer>
            <div className="right-wrapper">
              <TextField
                size="small"
                placeholder={t('Subscrib_Connect_Create_01')}
                value={keywordInput}
                onChange={handleKeywordChange}
                InputProps={{
                  startAdornment: (
                    <IconWrapper>
                      <SearchIcon width={16} height={16} color={colors['ic-gray-light']} />
                    </IconWrapper>
                  ),
                }}
              />
              {/* 구독 관리자는 본인 소유 구독만 관리 가능, 신규 추가 불가 */}
              {!authStore.isSubscriptionOwnerOnly && (
                <Button variant="contain" size="small" paddingHorizontal={16} onClick={handleAddModalOpen}>
                  {t('Subscrib_Create_01')}
                </Button>
              )}
            </div>
          </HeaderContainer>
          <SubscriptionTable
            isLoading={isSubscriptionListPageLoading}
            filter={queries}
            productList={productList}
            scrollTarget={scrollTarget}
            onUpdateFilter={handleTableFilterUpdate}
            endReached={handleEndReached}
          />
        </Content>
      </Container>

      <AddExistingSubscriptionModal open={isAddModalOpen} onClose={handleAddModalClose} />
    </>
  );
});

const Container = styled.section`
  width: 100%;
  height: calc(100vh - var(--manager-gnb-height));
  overflow: auto;
  padding: 28px 32px;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
`;
const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  margin-left: 8px;
`;
const SectionTitle = styled('strong')`
  ${fonts.Headline5};
`;
const InfoIconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: help;
`;
const CardContainer = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  overflow: auto;
  margin-bottom: 48px;
`;
const HeaderContainer = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  & > .right-wrapper {
    display: flex;
    gap: 8px;
  }
`;
const NumOfSubscription = styled('strong')`
  ${fonts.Headline7}
  color: ${({ theme }) => theme.colors['text-gray-main']};

  & span {
    margin-left: 4px;
    color: ${({ theme }) => theme.colors['text-primary-light']};
  }
`;
const IconWrapper = styled('div')`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
`;

const LeftContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
`;
